import type { Stage1Summary } from './contract';

export const stage1DemoReport = `# Stage 1 Evidence Report

- Run ID: \`20260614T010203Z\`
- Result fingerprint: \`066505b6d38e7e0f9884c84cbb32a53498e0f746d2232b6253d974e98fb91afe\`
- Decision authority: named reviewer
- Output: evidence readout only; no autonomous decision

## Inputs
- synthetic_linear_calibration: rows=5, columns=concentration, signal, potential, source_sha256=\`810cd6c5bd06623faaf7ec406c007173430c693073a2c90c1f714da49a05e74e\`

## Validation Panels
- synthetic_imprinting_control (imprinting_factor): source_sha256=\`7f22b153675e7505d2d3277af2f9d9424c4a0f84756375959b36e898569992c9\`

## Analyzer Metrics
- synthetic_linear_calibration (electroanalytical/swv): ok
  - intercept: \`2.0\`
  - linear_range_high: \`4.0\`
  - linear_range_low: \`0.0\`
  - lod: \`0.033\`
  - loq: \`0.1\`
  - n_points: \`5\`
  - n_points_fit: \`5\`
  - r_squared: \`0.9999\`
  - sensitivity_slope: \`100.0\`
  - flags: none
- synthetic_imprinting_control (validation/imprinting_factor): ok
  - imprinting_factor: \`0.5\`
  - mip_mean: \`200.0\`
  - nip_mean: \`400.0\`
  - flags: no_imprinting_effect

## Benchmark Gate
- Suite: \`method-validation\`
- Status: \`passed\`
- Tasks: \`7/7\` passed

## Strategy Scorecard
- imprinted_polymer: meets \`3/4\` goals
- enzyme_catalytic: meets \`1/4\` goals
- affinity_probe: meets \`4/4\` goals
- Note: decision support only; goals-met counts are not a recommendation - the reviewer decides

## Reviewer Decision
- Status: \`pending\`
- Decision: \`None\`
- Notes: The named reviewer records the final decision after reviewing real instrument evidence.

## Reproducibility
- Replay: \`researchharness replay /Users/analyst/research-hub/runs/20260614T010203Z\`
- Rerun template: \`researchharness stage1-evidence --input <instrument-export.csv> --validation-input <validation-panel.csv> --validation-method <method>\`
`;

export const stage1DemoSummary: Stage1Summary = {
  schema_version: 'stage1-summary.v1',
  run_id: '20260614T010203Z',
  run_dir: '/Users/analyst/research-hub/runs/20260614T010203Z',
  result_fingerprint: '066505b6d38e7e0f9884c84cbb32a53498e0f746d2232b6253d974e98fb91afe',
  inputs: [
    {
      schema_version: 'voltammetry-ingestion.v1',
      dataset_name: 'synthetic_linear_calibration',
      source_path: '/Users/analyst/research-hub/fixtures/linear_calibration.csv',
      source_sha256: '810cd6c5bd06623faaf7ec406c007173430c693073a2c90c1f714da49a05e74e',
      normalized_path:
        '/Users/analyst/research-hub/runs/20260614T010203Z/inputs/normalized/synthetic_linear_calibration.normalized.csv',
      normalized_sha256: 'd55f08528933843943d921b95127ee79aff6560f5463d1db9ab79b12a8373341',
      delimiter: 'semicolon',
      header_line_number: 5,
      skipped_preamble_lines: 4,
      row_count: 5,
      columns: ['concentration', 'signal', 'potential'],
      source_columns: {
        concentration: 'Concentration [uM]',
        signal: 'Peak current (nA)',
        potential: 'Potential (V)',
      },
      units: {
        concentration: 'uM',
        signal: 'nA',
        potential: 'V',
      },
      warnings: [],
      adapter: 'auto',
    },
  ],
  validation_inputs: [
    {
      schema_version: 'stage1-validation-input.v1',
      label: 'synthetic_imprinting_control',
      method: 'imprinting_factor',
      source_path: '/Users/analyst/research-hub/tests/fixtures/synthetic/imprinting_control.csv',
      workspace_path:
        '/Users/analyst/research-hub/runs/20260614T010203Z/artifacts/dataset/imprinting_control.csv',
      source_sha256: '7f22b153675e7505d2d3277af2f9d9424c4a0f84756375959b36e898569992c9',
      size_bytes: 44,
    },
  ],
  analyses: [
    {
      label: 'synthetic_linear_calibration',
      result: {
        schema_version: 'analysis-result.v1',
        analyzer: 'electroanalytical',
        method: 'swv',
        status: 'ok',
        input_path:
          '/Users/analyst/research-hub/runs/20260614T010203Z/inputs/normalized/synthetic_linear_calibration.normalized.csv',
        input_sha256: 'd55f08528933843943d921b95127ee79aff6560f5463d1db9ab79b12a8373341',
        metrics: {
          intercept: 2.0,
          linear_range_high: 4.0,
          linear_range_low: 0.0,
          lod: 0.033,
          loq: 0.1,
          n_points: 5,
          n_points_fit: 5,
          r_squared: 0.9999,
          sensitivity_slope: 100.0,
        },
        flags: [],
        notes: 'LOD/LOQ via ICH (3.3*s/S, 10*s/S); s = residual std of the linear fit',
      },
    },
    {
      label: 'synthetic_imprinting_control',
      result: {
        schema_version: 'analysis-result.v1',
        analyzer: 'validation',
        method: 'imprinting_factor',
        status: 'ok',
        input_path: '/Users/analyst/research-hub/tests/fixtures/synthetic/imprinting_control.csv',
        input_sha256: '7f22b153675e7505d2d3277af2f9d9424c4a0f84756375959b36e898569992c9',
        metrics: {
          imprinting_factor: 0.5,
          mip_mean: 200.0,
          nip_mean: 400.0,
        },
        flags: ['no_imprinting_effect'],
        notes: '',
      },
    },
  ],
  benchmark: {
    schema_version: 'benchmark-result.v1',
    suite: 'method-validation',
    passed: true,
    passed_count: 7,
    total: 7,
    tasks: [
      {
        name: 'linear_calibration',
        passed: true,
        status: 'ok',
        checks: [
          { metric: 'r_squared', actual: 0.9999, expected_min: 0.99, expected_max: null, passed: true },
          { metric: 'sensitivity_slope', actual: 100.0, expected_min: 95, expected_max: 105, passed: true },
          { metric: 'lod', actual: 0.033, expected_min: 0, expected_max: null, passed: true },
        ],
      },
      {
        name: 'linearity_failure',
        passed: true,
        status: 'ok',
        checks: [
          { metric: 'r_squared', actual: 0.0128, expected_min: null, expected_max: 0.5, passed: true },
          { metric: 'n_points_fit', actual: 5, expected_min: 5, expected_max: 5, passed: true },
        ],
      },
      {
        name: 'amperometric_calibration',
        passed: true,
        status: 'ok',
        checks: [
          { metric: 'r_squared', actual: 1.0, expected_min: 0.99, expected_max: null, passed: true },
          { metric: 'sensitivity_slope', actual: 10.0, expected_min: 9, expected_max: 11, passed: true },
        ],
      },
      {
        name: 'substrate_inhibition',
        passed: true,
        status: 'ok',
        checks: [
          { metric: 'peak_concentration', actual: 2.0, expected_min: 1.9, expected_max: 2.1, passed: true },
          { metric: 'post_peak_drop_percent', actual: 30.0, expected_min: 25, expected_max: 35, passed: true },
          { metric: 'low_range_r_squared', actual: 0.999, expected_min: 0.99, expected_max: null, passed: true },
        ],
      },
      {
        name: 'eis_rct',
        passed: true,
        status: 'ok',
        checks: [
          {
            metric: 'solution_resistance_ohm',
            actual: 100.0,
            expected_min: 90,
            expected_max: 110,
            passed: true,
          },
          {
            metric: 'charge_transfer_resistance_ohm',
            actual: 400.0,
            expected_min: 390,
            expected_max: 410,
            passed: true,
          },
          { metric: 'nyquist_arc_height_ohm', actual: 205.0, expected_min: 190, expected_max: 220, passed: true },
        ],
      },
      {
        name: 'imprinting_control',
        passed: true,
        status: 'ok',
        checks: [
          { metric: 'mip_mean', actual: 200.0, expected_min: 190, expected_max: 210, passed: true },
          { metric: 'nip_mean', actual: 400.0, expected_min: 390, expected_max: 410, passed: true },
          { metric: 'imprinting_factor', actual: 0.5, expected_min: 0, expected_max: 1, passed: true },
        ],
      },
      {
        name: 'equilibration',
        passed: true,
        status: 'ok',
        checks: [
          { metric: 't95_seconds', actual: 75.0, expected_min: 70, expected_max: 80, passed: true },
          { metric: 'solubility_fraction', actual: 0.5, expected_min: 0.45, expected_max: 0.55, passed: true },
        ],
      },
    ],
  },
  strategy_scorecard: {
    schema_version: 'strategy-comparison.v1',
    name: 'biosensor-strategy-comparison',
    note: 'decision support only; goals-met counts are not a recommendation - the reviewer decides',
    goals: [
      { name: 'lod_nM', direction: 'lower_is_better', target: 50 },
      { name: 'stability_hours', direction: 'higher_is_better', target: 72 },
      { name: 'reversible', direction: 'boolean', target: null },
      { name: 'selective_vs_interferent', direction: 'boolean', target: null },
    ],
    strategies: [
      {
        strategy: 'imprinted_polymer',
        source: 'Example literature summary A (illustrative only)',
        goals_met: 3,
        goals_assessed: 4,
        goals: [
          { goal: 'lod_nM', direction: 'lower_is_better', target: 50, value: 0.5, meets: true },
          { goal: 'stability_hours', direction: 'higher_is_better', target: 72, value: 168, meets: true },
          { goal: 'reversible', direction: 'boolean', target: null, value: true, meets: true },
          { goal: 'selective_vs_interferent', direction: 'boolean', target: null, value: false, meets: false },
        ],
      },
      {
        strategy: 'enzyme_catalytic',
        source: 'Example literature summary B (illustrative only)',
        goals_met: 1,
        goals_assessed: 4,
        goals: [
          { goal: 'lod_nM', direction: 'lower_is_better', target: 50, value: 1200, meets: false },
          { goal: 'stability_hours', direction: 'higher_is_better', target: 72, value: 24, meets: false },
          { goal: 'reversible', direction: 'boolean', target: null, value: false, meets: false },
          { goal: 'selective_vs_interferent', direction: 'boolean', target: null, value: true, meets: true },
        ],
      },
      {
        strategy: 'affinity_probe',
        source: 'Example literature summary C (illustrative only)',
        goals_met: 4,
        goals_assessed: 4,
        goals: [
          { goal: 'lod_nM', direction: 'lower_is_better', target: 50, value: 2.0, meets: true },
          { goal: 'stability_hours', direction: 'higher_is_better', target: 72, value: 72, meets: true },
          { goal: 'reversible', direction: 'boolean', target: null, value: true, meets: true },
          { goal: 'selective_vs_interferent', direction: 'boolean', target: null, value: true, meets: true },
        ],
      },
    ],
  },
  verification: {
    passed: true,
    score: 1.0,
    failed_categories: [],
  },
  caveats: [
    'CSV ingestion was normalized by adapter; review source-column mapping before supervisor use.',
    "Evidence readout only: the scorecard does not make the reviewer's decision.",
    'synthetic_imprinting_control: analyzer flag no_imprinting_effect.',
  ],
  reviewer_decision: {
    status: 'pending',
    decision: null,
    notes: 'The named reviewer records the final decision after reviewing real instrument evidence.',
  },
  reproducibility: {
    replay_command: 'researchharness replay /Users/analyst/research-hub/runs/20260614T010203Z',
    rerun_command_template:
      'researchharness stage1-evidence --input <instrument-export.csv> --validation-input <validation-panel.csv> --validation-method <method>',
  },
};
